import type { AddressDetail, AddressSuggestions } from '@bk/contracts';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';

const newSession = () => crypto.randomUUID();

/**
 * Indirizzo scelto dai suggerimenti di Google mentre si scrive (ADR 0028): niente CAP, comune o provincia a mano. Si
 * scrive, si tocca il suggerimento giusto e arriva l'indirizzo già scomposto. `value` è come lo si mostra.
 * `onUnavailable` scatta se il server non ha il servizio: allora chi lo usa mostra i campi a mano.
 */
export function AddressInput({
  value,
  onPick,
  onUnavailable,
  autoFocus,
}: {
  value: string;
  onPick: (a: AddressDetail) => void;
  onUnavailable?: () => void;
  autoFocus?: boolean;
}) {
  const { t } = useTenant();
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(!value);
  const [list, setList] = useState<AddressSuggestions['data']>([]);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(false);
  // Una sessione per indirizzo cercato: Google conta suggerimenti e dettaglio come una ricerca sola.
  const session = useRef(newSession());
  useEffect(() => {
    const q = text.trim();
    if (q.length < 3) {
      setList([]);
      return;
    }
    const timer = setTimeout(() => {
      api<AddressSuggestions>(t(`/addresses?q=${encodeURIComponent(q)}&session=${session.current}`))
        .then((r) => {
          if (!r.disponibile) onUnavailable?.();
          setList(r.data);
          setError('');
        })
        .catch((e) => setError(errorMessage(e)));
    }, 250);
    return () => clearTimeout(timer);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (id: string) => {
    setPicking(true);
    try {
      const a = await api<AddressDetail>(
        t(`/addresses/${encodeURIComponent(id)}?session=${session.current}`),
      );
      session.current = newSession();
      setList([]);
      setText('');
      setEditing(false);
      onPick(a);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setPicking(false);
    }
  };
  if (!editing)
    return (
      <div className="flex items-center gap-3 rounded-lg border border-black/10 bg-black/[0.02] px-3 py-2 text-sm">
        <span className="min-w-0 flex-1">📍 {value}</span>
        <button
          type="button"
          className="text-xs text-accent hover:underline"
          onClick={() => setEditing(true)}
        >
          cambia
        </button>
      </div>
    );
  return (
    <div className="relative">
      <input
        autoFocus={autoFocus}
        className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
        placeholder="Scrivi l’indirizzo e scegli tra i suggerimenti"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (list[0]) void pick(list[0].id);
          }
          if (e.key === 'Escape' && value) setEditing(false);
        }}
        aria-autocomplete="list"
      />
      {list.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-black/10 bg-white shadow-lg">
          {list.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                disabled={picking}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-accent/[0.06] disabled:opacity-50"
                onClick={() => void pick(s.id)}
              >
                <span className="font-medium">{s.testo}</span>
                {s.secondario && <span className="block text-xs text-ink/50">{s.secondario}</span>}
              </button>
            </li>
          ))}
          <li className="px-3 py-1 text-right text-[10px] text-ink/35">suggerimenti di Google</li>
        </ul>
      )}
      {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
    </div>
  );
}

/** L'indirizzo di una sede in una riga, per mostrarlo nel campo. */
export function addressLine(a: {
  address: string | null;
  postal_code: string | null;
  municipality: string | null;
  province: string | null;
}): string {
  const town = [a.postal_code, a.municipality].filter(Boolean).join(' ');
  const place = [town, a.province && a.province !== a.municipality ? `(${a.province})` : null]
    .filter(Boolean)
    .join(' ');
  return [a.address, place].filter(Boolean).join(', ');
}
